import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ModalComponent } from 'app/shared/components/modal/modal.component';
import { Plant } from 'app/shared/models/plant.model';
import { Line } from 'app/shared/models/line.model';
import { LineService } from '../../../../../../core/services/line.service';
import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";
import { PickList } from "primeng/picklist";

/**
 * Modal form for creating / updating {@link Plant} objects.
 */
@Component({
    selector: 'app-plant-create-modal',
    templateUrl: './plant-create-modal.component.html',
    styleUrls: ['./plant-create-modal.component.scss']
})
export class PlantCreateModalComponent implements OnInit {
    @ViewChild(ModalComponent, {static: true}) modal: ModalComponent;
    @ViewChild(PickList, {static: true}) pickList: PickList;

    @Output('onCreate')
    onCreate: EventEmitter<any> = new EventEmitter();
    @Output('onUpdate')
    onUpdate: EventEmitter<any> = new EventEmitter();
    @Output('onDelete')
    onDelete: EventEmitter<any> = new EventEmitter();

    _logger = Log.create(PlantCreateModalComponent.name);

    @Input() isLoading;

    @Input()
    lines: Line[] = [];
    plant = new Plant();

    form: UntypedFormGroup;
    selectedLines: any[] = [];
    previousPlantName = '';
    validationErrors;
    showValidations = false;

    // Indicates whether the modal should be opened in duplicate mode
    duplicateMode = false;

    constructor(private lineService: LineService,
                private permissionService: PermissionService) {
    }

    ngOnInit() {
        this.form = new UntypedFormGroup({
            name: new UntypedFormControl('', [<any>Validators.required]),
            description: new UntypedFormControl('', [<any>Validators.required]),
            documentsLocation: new UntypedFormControl('')
        });
        if (this.shouldRenderModifiers()) {
            this.getLines();
        }
    }

    /**
     * Opens the modal and sets up it's specific requirements.
     * @param {any} plant
     */
    open(plant = new Plant(), duplicate = false) {
        this.reset();
        this.loadFormValue(plant);
        if (this.shouldRenderModifiers()) {
            this.getLines();
        }
        this.duplicateMode = duplicate;
        this.modal.open();
    }

    close() {
        this.modal.close();
    }

    loadFormValue(plant) {
        this.plant = plant;
        this.previousPlantName = plant.name;
        this.form.setValue({name: plant.name, description: plant.description, documentsLocation: plant.documentsLocation });
    }

    onPlantLinesSelectionChange(event) {
        this.plant.lines = this.selectedLines;
    }

    createPlant() {
        if (!this.isFormValid()) {
            return;
        }
        this.onCreate.emit(this.getFormValue());
    }

    updatePlant() {
        if (!this.isFormValid()) {
            return;
        }
        this.onUpdate.emit({
            previousPlantName: this.previousPlantName,
            newPlant: this.getFormValue()
        });
    }

    deletePlant() {
        this.onDelete.emit(this.getFormValue());
    }

    isPlantInUse() {
        return this.plant.inUse;
    }

    getFormValue() {
        return Object.assign({}, this.plant, this.form.getRawValue());
    }

    get isEditMode() {
        return !!this.plant.id && !this.duplicateMode;
    }

    private getLines() {
        this.lineService.getAllLines().subscribe(
            (data: any) => {
                this.lines = data.items;
            },
            (error: any) => {
                this._logger.error(error);
            }
        );
    }

    private reset() {
        this.showValidations = false;
        this.validationErrors = {};
        this.form.reset();
        this.duplicateMode = false;
    }

    private isFormValid() {
        this.validationErrors = {};
        this.showValidations = true;
        let isValid = true;

        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.controls[key];
            if (control.invalid) {
                isValid = false;
            }
        });

        return isValid;
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_PLANTS');
    }

}
